import React, { useContext, useEffect, useState } from 'react';
import Slider from 'react-slick';
import { CountryCode, Variant } from '@dayetopia/library/types';
import { PrismicImage } from '@prismicio/react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { RichText } from 'prismic-reactjs';
import styled, { keyframes } from 'styled-components';
import { Context } from '@components/Context';
import { useModal } from '@components/ModalContext';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import HeaderIcons from '@components/shared/HeaderIcons';
import {
  CBD_REGULAR_TAMPON_KEY,
  CBD_REGULAR_TAMPON_NAME,
  CBD_SUPER_TAMPON_KEY,
  CBD_SUPER_TAMPON_NAME,
  NAKED_REGULAR_TAMPON_KEY,
  NAKED_REGULAR_TAMPON_NAME,
  NAKED_SUPER_TAMPON_KEY,
  NAKED_SUPER_TAMPON_NAME
} from '@constants';
import {
  BadgeSizeType,
  CartItem,
  CartItemType,
  ProductCode,
  Promotion,
  SliceHeroSliderItem,
  default12TamponConfiguration,
  default18TamponConfiguration
} from '@contracts';
import { useCountry } from '@hooks/useCountry';
import IconSun from '@images/icon-sun.svg';
import ArrowNext from '@images/storefront/arrow-next.svg';
import ArrowPrev from '@images/storefront/arrow-prev.svg';
import { getBoxPrice } from '@utils/boxPrices';
import { getUspIconsFromData } from '@utils/helpers';
import { getProductPrice } from '@utils/price';
import { selectPromotionTracking } from '@utils/tracking';
import useNavigate from '@utils/useNavigate';
import Badge from './Badge';
import ConsultationsModal from './ConsultationsModal';
import { TextVariant, TEXT_STYLES } from './design/Typography';
import HeroAuxButton from './HeroAuxButton';
import { Gradient2Style } from './SharedStyles';
import useDesktop from './useDesktop';

const NextArrow = (props: any) => {
  const { className, onClick } = props;
  return <ArrowNext onClick={onClick} className={className} />;
};

const PrevArrow = (props: any) => {
  const { className, onClick } = props;
  return <ArrowPrev onClick={onClick} className={className} />;
};

interface SlideProps {
  content: SliceHeroSliderItem;
  index: number;
  transitionSpeed?: number;
}

const Slide: React.FC<SlideProps> = ({ content, index, transitionSpeed = undefined }) => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const { openModal } = useModal();
  const { state } = useContext(Context);
  const { country, currency, firebaseAuth } = state;
  const isDesktop = useDesktop(1024);
  const button1Text = content?.button_1_text?.text;
  const hideButton2 = content?.hide_button_2;
  const productCode = content?.product_code?.text;
  const imagePadding = content?.image_padding;
  const mobileReverse = content?.mobile_reverse;
  const auxImage = content?.aux_image;
  const designStyle = content?.design_style ?? 'standard';
  const [price, setPrice] = useState(-1);
  const [cbdTamponPrice, setCbdTamponPrice] = useState(0);
  const [nakedTamponPrice, setNakedTamponPrice] = useState(0);
  const boxVariants = country === CountryCode.US ? default18TamponConfiguration : default12TamponConfiguration;
  const desktopImage = content?.image;
  const mobileImage = content?.mobile_image?.url ? content?.mobile_image : content?.image;
  const hideMobileImage = content?.hide_mobile_image;
  const subtitle = content?.subtitle?.text;
  const absoluteImage = !imagePadding;
  const buttonColor = content?.button_color ?? Colors.navyBlue;
  const isFsaHsaEligible = country === CountryCode.US && content.fsa_hsa_eligible;

  useEffect(() => {
    if (!productCode || productCode === 'ppc') return;
    handleFetchPrice();
  }, [productCode]);

  const getTamponPrices = async () => {
    try {
      const cbdPrice = await getBoxPrice({ cbdregular: 1 } as Variant, currency, country);
      setCbdTamponPrice(cbdPrice);
      const nakedPrice = await getBoxPrice({ nakedregular: 1 } as Variant, currency, country);
      setNakedTamponPrice(nakedPrice);
    } catch (error) {
      console.error(error);
    }
  };

  const onLink1Click = () => {
    const promotion = getPromotionItem();
    const userEmail = firebaseAuth?.currentUser?.email ?? '';
    selectPromotionTracking([promotion], userEmail);

    const link = content?.button_1_link?.text;

    if (link === '#free-consultation') {
      // check if the link corresponds to a trigger for the consultation modal
      openModal(t('free-consultations.book-a-free-consultation'), <ConsultationsModal />);
      return;
    }

    const url = link;

    const urlReplacements = {
      '/products/tampons/': '/products/organic-cbd-nude-tampons/',
      '/products/at-home-vaginal-screening/': '/products/vaginal-testing/'
    };

    const shouldReplaceUrl = country === CountryCode.US && urlReplacements[url];
    navigateWrapper(shouldReplaceUrl ? urlReplacements[url] : url);
  };
  const onAddToCartClick = () => {
    const promotion = getPromotionItem();
    const userEmail = firebaseAuth?.currentUser?.email ?? '';
    selectPromotionTracking([promotion], userEmail);
  };

  const getPromotionItem = () => {
    const promotion: Promotion = {
      promotion_id: content?.product_code?.text,
      name: content?.subtitle?.text,
      creative: content?.title?.text,
      position: 'top_banner',
      products: []
    };
    return promotion;
  };

  const handleFetchPrice = async () => {
    if (productCode === ProductCode.TamponBox) {
      getTamponPrices();
      const boxPrice = await getBoxPrice(boxVariants, currency, country);
      setPrice(boxPrice);
    } else {
      const productPrice = await getProductPrice(productCode, currency, country);
      setPrice(productPrice.price);
    }
  };

  const getBoxItems = () => {
    const { cbdsuper, cbdregular, nakedsuper, nakedregular } = boxVariants;
    return [
      { count: cbdsuper, key: CBD_SUPER_TAMPON_KEY, name: CBD_SUPER_TAMPON_NAME, price: cbdTamponPrice },
      { count: cbdregular, key: CBD_REGULAR_TAMPON_KEY, name: CBD_REGULAR_TAMPON_NAME, price: cbdTamponPrice },
      { count: nakedsuper, key: NAKED_SUPER_TAMPON_KEY, name: NAKED_SUPER_TAMPON_NAME, price: nakedTamponPrice },
      { count: nakedregular, key: NAKED_REGULAR_TAMPON_KEY, name: NAKED_REGULAR_TAMPON_NAME, price: nakedTamponPrice }
    ];
  };

  const generateBoxSKU = () => {
    const { cbdsuper, cbdregular, nakedsuper, nakedregular } = boxVariants;
    return `${cbdregular}${cbdsuper}${nakedregular}${nakedsuper}`;
  };

  const generateCartItem = () => {
    if (productCode === ProductCode.TamponBox) {
      // TODO: Handle US tampon box product creation
      const boxItems = getBoxItems();
      const boxSKU = generateBoxSKU();
      const cartItem = {
        boxItems,
        id: `tampon_box_${boxSKU}`,
        type: CartItemType.OneOffPurchase,
        price,
        productCode: 'tampon_box',
        quantity: 1,
        editable: true,
        unique: false
      } as CartItem;
      return cartItem;
    }
    return new CartItem(productCode, productCode, price);
  };

  const renderAuxImage = () => {
    if (designStyle === 'headline') return null;
    if (auxImage?.gatsbyImageData) {
      return <GatsbyImage image={auxImage.gatsbyImageData} alt={auxImage.alt} loading="eager" />;
    }
    return <IconSun />;
  };

  return (
    <SlideContainer $background={content?.background?.text} $hideMobileImage={hideMobileImage} $mobileReverse={mobileReverse}>
      <SlideInner $mobileReverse={mobileReverse} $absoluteImage={absoluteImage}>
        <ContentContainer
          $color={Colors.navyBlue}
          className="slide-content-container"
          $transitionSpeed={transitionSpeed ? transitionSpeed + 200 : undefined}
          $alignLeft={!!content?.desktop_text_align_left}
        >
          {content?.coming_soon ? (
            <ComingSoonWrap>
              <Badge sizeTypes={[BadgeSizeType.Small]} text={t('components.coming-soon')} />
            </ComingSoonWrap>
          ) : null}
          <TitleWrap
            $designStyle={designStyle}
            $hasSubtitle={subtitle !== ''}
            $titleMaxWidth={content?.title_max_width}
            $titleMobileMaxWidth={content?.title_mobile_max_width}
          >
            {content?.no_aux_image ? null : (
              <AuxImageWrap
                $auxImageMobile={content?.aux_image_mobile}
                $auxImageMaxWidth={content?.aux_image_max_width}
                $auxImageMobileMaxWidth={content?.aux_image_mobile_max_width}
              >
                {renderAuxImage()}
              </AuxImageWrap>
            )}
            <ContentSubtitle $designStyle={designStyle}>{subtitle}</ContentSubtitle>
            {RichText.render(content?.title?.richText)}
          </TitleWrap>
          {content?.info?.text ? (
            <ContentInfo
              $infoMaxWidth={content?.info_max_width}
              $infoMobileMaxWidth={content?.info_mobile_max_width}
              dangerouslySetInnerHTML={{ __html: content?.info?.text }}
            />
          ) : (
            <>
              {isDesktop && (
                <USPsWrap>
                  <HeaderIcons
                    icons={getUspIconsFromData(content)}
                    isVerticalDirection={false}
                    background={content?.icons_background}
                    iconSize="20px"
                  />
                </USPsWrap>
              )}
            </>
          )}
          <ButtonRow>
            {button1Text && (
              <ButtonStyled $color={buttonColor} onClick={onLink1Click}>
                {button1Text}
              </ButtonStyled>
            )}
            {!hideButton2 && <HeroAuxButton productCode={productCode} generateCartItem={generateCartItem} onAddToCartClick={onAddToCartClick} />}
          </ButtonRow>
          {isFsaHsaEligible && <FSAHSANotice>{t('components.fsa-hsa-notice')}</FSAHSANotice>}
        </ContentContainer>
        <ImageContainer
          className="slide-image-container"
          $transitionSpeed={(transitionSpeed ?? 0) + 200}
          $hideOnMobile={hideMobileImage && !isDesktop}
          $absoluteImage={absoluteImage}
          $mobilePadding={content?.image_mobile_padding}
          $mobileMaxWidth={content?.image_mobile_max_width}
        >
          {desktopImage ? (
            <PrismicImage
              className="desktop-image"
              field={desktopImage}
              alt={desktopImage.alt ?? ''}
              width={desktopImage.dimensions?.width}
              height={desktopImage.dimensions?.height}
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchpriority={index === 0 ? 'high' : 'low'}
            />
          ) : null}
          {mobileImage ? (
            <PrismicImage
              className="mobile-image"
              field={mobileImage}
              alt={mobileImage.alt ?? ''}
              width={mobileImage.dimensions?.width}
              height={mobileImage.dimensions?.height}
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchpriority={index === 0 ? 'high' : 'low'}
            />
          ) : null}
        </ImageContainer>
      </SlideInner>
      {content?.background_image?.url && (
        <BackgroundLayer className="slide-background-image" $backgroundImage={content?.background_image.url} $transitionSpeed={transitionSpeed} />
      )}
    </SlideContainer>
  );
};

interface Props {
  slice: {
    items: SliceHeroSliderItem[];
  };
}

const HeroSlider: React.FC<Props> = ({ slice }) => {
  const { country } = useCountry();
  const sortedSlides = slice.items.sort((a, b) => (a?.priorityNum || 99) - (b?.priorityNum || 99));

  const filteredSlides = sortedSlides?.filter((item) => {
    const showInCountries = item.show_in_countries?.text ?? '';

    // If no country filter is set for the item, include it
    if (!showInCountries) {
      return true;
    }

    // If the country filter is prefixed with '-' and matches the specified country, exclude it
    // Otherwise, include it (because it's for a different country)
    if (showInCountries.startsWith('-')) {
      const excludedCountry = showInCountries.slice(1);
      if (excludedCountry === country) {
        return false;
      }
      return true;
    }

    // Include the item if the country matches the specified country
    if (showInCountries === country) {
      return true;
    }

    // Exclude the item by default if none of the above conditions are met
    return false;
  });

  const oneSlide = filteredSlides.length > 0 ? filteredSlides[0] : null;
  const slides = oneSlide ? [oneSlide] : [];
  const autoplay = 5000;
  const transition = 700;

  const settings = {
    dots: true,
    dotsClass: 'slick-dots slick-thumb',
    infinite: true,
    speed: transition,
    autoplay: true,
    fade: false,
    autoplaySpeed: autoplay,
    swipeToSlide: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    nextArrow: (
      <ArrowWrap>
        <NextArrow />
      </ArrowWrap>
    ),
    prevArrow: (
      <ArrowWrap>
        <PrevArrow />
      </ArrowWrap>
    )
  };

  // if (isDesktop) settings = { ...settings, customPaging };

  if (slides.length === 1) {
    return (
      <SliderWrap $autoplayTimer={autoplay - 200} $transitionSpeed={transition - 200}>
        <SingleSlideWrap>
          <Slide key={0} index={0} content={slides[0]} />
        </SingleSlideWrap>
      </SliderWrap>
    );
  }

  return (
    <SliderWrap $autoplayTimer={autoplay - 200} $transitionSpeed={transition - 200}>
      {slides.length > 1 ? (
        <Slider {...settings}>
          {slides.map((item, index) => (
            <Slide key={index} index={index} content={item} transitionSpeed={transition - 200} />
          ))}
        </Slider>
      ) : (
        <SingleSlideWrap>
          <Slide key={0} index={0} content={slides[0]} />
        </SingleSlideWrap>
      )}
    </SliderWrap>
  );
};

export default HeroSlider;

const ScaleX = keyframes`
    0% {
    transform: scaleX(0);
  }

  100% {
    transform: scaleX(1);
  }
`;

const SingleSlideWrap = styled.div`
  display: flex;
  width: 100%;
  z-index: 99;
`;

const SliderWrap = styled.div<{ $autoplayTimer: number; $transitionSpeed?: number }>`
  width: 100%;
  position: relative;
  z-index: 1;
  overflow: hidden;

  .slick-track {
    display: flex;
    align-items: flex-start;
  }

  .slick-arrow {
    position: absolute;
    z-index: 99;
    bottom: 50%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0;
    border-radius: 0;
    gap: 10px;
    width: 40px;
    height: 40px;
    background: ${Colors.cream};

    @media (max-width: 1024px) {
      display: none !important;
    }
  }

  .slick-slide {
    z-index: 1;
  }

  .slick-slide.slick-active {
    z-index: 2;
  }

  .slick-active .slide-content-container {
    opacity: 1;
    bottom: 80px;
    left: 0;

    @media (max-width: 1024px) {
      bottom: 0;
    }
  }

  .slick-active .slide-image-container {
    opacity: 1;
    bottom: 0;
    right: 0;
  }

  .slick-active .slide-background-image {
    opacity: 1;
  }

  .slick-prev {
    left: 0;
    border-radius: 0 5px 5px 0;
  }

  .slick-next {
    right: 0;
    border-radius: 5px 0 0 5px;
  }

  .slick-dots {
    display: flex !important;
    flex-wrap: nowrap;
    justify-content: flex-start;
    position: relative;
    z-index: 99;
    width: 1400px;
    max-width: 90%;
    margin: 0 auto;
    bottom: 60px;

    @media (max-width: 1024px) {
      justify-content: center;
      bottom: 30px;
    }
  }

  .slick-dots li {
    height: 4px;
    width: 30px;
    margin: 0 10px 0 0;

    button {
      width: 100%;
      height: 100%;
      padding: 0;
      background: ${Colors.navyBlue}20;
      border-radius: 2px;
      overflow: hidden;

      &:before {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: block;
        position: absolute;
        bottom: 0;
        z-index: 99;
        transform: scaleX(0);
        transform-origin: left 50%;
        background-color: ${Colors.gold};
        border-radius: 2px;
        overflow: hidden;
      }
    }

    &.slick-active button {
      background: ${Colors.navyBlue}10;

      &:before {
        animation: ${ScaleX} ${(props) => `${props.$autoplayTimer}ms linear ${props.$transitionSpeed}ms 1 forwards`};
      }
    }

    &:hover {
      opacity: 0.7;
    }

    & > a {
      display: inline-block;
      margin-bottom: 0;
      height: 100%;
      background: ${Colors.cream};
      box-sizing: border-box;

      & > div {
        height: 100%;
      }
    }
  }
`;

const ArrowWrap = styled.div`
  width: 30px;
  height: 30px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: ${Colors.ecru};
  border-radius: 15px;
`;

const ComingSoonWrap = styled.div`
  display: flex;

  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

const SlideContainer = styled.div<{ $background: string; $mobileReverse: boolean; $hideMobileImage: boolean }>`
  background: ${(props) => (props.$background ? props.$background : 'linear-gradient(180deg, #f9d07c 0%, #fffbe7 100%)')};
  width: 100%;

  .icon-text {
    text-transform: none;
    font-size: 12px;
    letter-spacing: 0.02em;
    font-weight: 700;
  }

  @media (max-width: 1024px) {
    padding-top: 80px;
    padding-bottom: ${(props) => (props.$hideMobileImage || props.$mobileReverse ? '30px' : '0')};

    .icon-rows-wrap {
      padding: 0 !important;

      .icon-wrap {
        max-width: 100px;

        .gatsby-image-wrapper {
          min-height: 30px !important;
        }
      }
    }
  }
`;

const SlideInner = styled.div<{ $mobileReverse: boolean; $absoluteImage: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.$absoluteImage ? 'flex-start' : 'space-evenly')};
  align-items: center;
  position: relative;
  flex-wrap: ${(props) => (props.$absoluteImage ? 'wrap' : 'nowrap')};
  width: 100%;
  max-width: 1400px;
  height: ${(props) => (props.$absoluteImage ? '650px' : 'auto')};
  padding-left: 120px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    height: auto;
    display: flex;
    padding: 0;
    gap: 10px;
    margin: 0 auto;
    max-width: 100%;
    flex-direction: ${(props) => (props.$mobileReverse ? 'column-reverse' : 'column')};
  }
`;

const BackgroundLayer = styled.div<{ $backgroundImage: string; $transitionSpeed?: number }>`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${(props) => (props.$backgroundImage ? `url(${props.$backgroundImage})` : 'none')};
  background-position: top center;
  background-repeat: no-repeat;
  opacity: ${(props) => (props.$transitionSpeed ? '0' : '1')};
  transition: opacity 1700ms ease-out;
  transition-delay: ${(props) => `${props.$transitionSpeed}ms`};
  background-size: cover;
`;

const ContentContainer = styled.div<{ $transitionSpeed?: number; $alignLeft: boolean; $color?: string }>`
  position: relative;
  z-index: 3;
  opacity: ${(props) => (props.$transitionSpeed ? '0' : '1')};
  transition: opacity 1700ms ease-out, bottom 1700ms cubic-bezier(0, 0.84, 0.44, 1), left 1700ms cubic-bezier(0, 0.84, 0.44, 1);
  transition-delay: ${(props) => `${props.$transitionSpeed}ms`};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: ${(props) => (props.$alignLeft ? 'left' : 'center')};
  padding: 0;
  width: 490px;
  gap: 32px;
  color: ${(props) => props.$color};

  @media (max-width: 1024px) {
    position: relative;
    width: 100%;
    max-width: 100%;
    padding: 0 30px;
    gap: 24px;
    align-items: center;
    text-align: center;
  }
`;

const ImageContainer = styled.div<{
  $transitionSpeed?: number;
  $hideOnMobile: boolean;
  $absoluteImage: boolean;
  $mobilePadding?: string;
  $mobileMaxWidth?: string;
}>`
  display: block;

  ${(props) =>
    props.$absoluteImage
      ? `
    position: absolute;
    z-index: 2;
    right: -20px;
    bottom: -20px;
    height: 100%;
    max-width: 57%;

    & img {
      max-width: 100%;
      height: 100%;
    }
  `
      : `
    padding: 8% 4% 8% 0;

    & img {
      max-width: 520px;
      height: auto;
    }
  `};

  img {
    width: auto;
    object-fit: contain;
    object-position: bottom;

    &.mobile-image {
      display: none;

      @media (max-width: 1024px) {
        display: block;
      }
    }

    &.desktop-image {
      display: block;

      @media (max-width: 1024px) {
        display: none;
      }
    }
  }

  @media (max-width: 1200px) {
    margin-right: 20px;
    padding: 0;
  }

  @media (max-width: 1024px) {
    display: ${(props) => (props.$hideOnMobile ? 'none' : 'flex')};
    justify-content: center;
    position: relative;
    width: 100%;
    padding: ${(props) => props.$mobilePadding || '0'};
    max-width: ${(props) => props.$mobileMaxWidth || '100%'};
    height: auto;
    right: 0;
    bottom: 0;
    text-align: center;
    margin-right: 0;

    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }
`;

const TitleWrap = styled.div<{ $hasSubtitle: boolean; $titleMaxWidth?: string; $titleMobileMaxWidth?: string; $designStyle?: string }>`
  display: flex;
  flex-direction: ${(props) => (props.$designStyle === 'headline' ? 'column-reverse' : 'column')};
  align-items: center;
  justify-content: center;
  gap: 16px;

  @media (max-width: 1024px) {
    flex-direction: ${(props) => (props.$hasSubtitle ? 'column-reverse' : 'column')};
    max-width: 100%;
  }

  & > svg {
    width: 47px;

    @media (max-width: 1024px) {
      display: ${(props) => (props.$hasSubtitle ? 'none' : 'block')};
    }
  }

  & > h2 {
    font-family: 'Domaine';
    text-shadow: 0px 4px 30px rgba(0, 0, 0, 0.05);
    font-size: 50px;
    font-style: normal;
    font-weight: 600;
    line-height: 110%; /* 55px */
    letter-spacing: -0.5px;
    color: inherit;
    -webkit-font-smoothing: antialiased;
    max-width: ${(props) => props.$titleMaxWidth || '100%'};

    & > span.pink {
      color: ${Colors.lilac};
    }

    & > span.gradient {
      background: -webkit-gradient(linear, left top, right top, from(rgb(209, 106, 169)), to(rgb(0, 29, 59)));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    & > span.gradient2 {
      ${Gradient2Style}
    }

    @media (max-width: 1024px) {
      max-width: ${(props) => props.$titleMobileMaxWidth || '100%'};
      font-size: 34px;
    }
  }
`;

const ContentSubtitle = styled.h5<{ $designStyle?: string }>`
  ${({ $designStyle }) => {
    const isHeadline = $designStyle === 'headline';
    return `
      font-family: 'BrownStd';
      font-style: normal;
      max-width: ${isHeadline ? '70%' : '100%'};
      font-weight: ${isHeadline ? '400' : '700'};
      font-size: ${isHeadline ? '16px' : '14px'};
      line-height: 140%;
      display: flex;
      align-items: center;
      letter-spacing: ${isHeadline ? 'inherit' : '1.5px'};
      text-transform: ${isHeadline ? 'none' : 'uppercase'};
      color: inherit;
    `;
  }}

  @media (max-width: 1024px) {
    font-size: 13px;
    font-weight: 400;
    max-width: 100%;
    text-transform: unset;
    align-self: center;
    letter-spacing: 0;
  }
`;

const ContentInfo = styled.div<{ $infoMaxWidth?: string; $infoMobileMaxWidth?: string }>`
  font-family: 'BrownStd';
  font-size: 18px;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: 0;
  text-align: center;
  color: inherit;
  margin-bottom: 10px;
  max-width: ${(props) => props.$infoMaxWidth || '100%'};

  @media (max-width: 1024px) {
    max-width: ${(props) => props.$infoMobileMaxWidth || '100%'};
    font-size: 16px;
  }
`;

const USPsWrap = styled.div`
  display: block;
  margin-bottom: 10px;

  &:empty {
    display: none;
  }

  .icon-rows-wrap > div {
    min-width: 156px;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 20px;
  z-index: 99;
`;

const ButtonStyled = styled(Button)<{ $color: string }>`
  background: ${(props) => props.$color};
  border-radius: 5px;

  &:hover {
    background: ${(props) => (props.$color !== Colors.lilac ? Colors.lilac : Colors.forestGreen)};
  }
`;

const AuxImageWrap = styled.div<{
  $auxImageMobile?: boolean;
  $auxImageMaxWidth?: string;
  $auxImageMobileMaxWidth?: string;
}>`
  max-width: ${({ $auxImageMaxWidth }) => $auxImageMaxWidth || 'none'};

  &:empty {
    display: none;
  }

  ${({ $auxImageMobile }) =>
    !$auxImageMobile
      ? `@media (max-width: 1024px) {
        display: none;
      }
    `
      : null}
  ${({ $auxImageMobileMaxWidth }) =>
    $auxImageMobileMaxWidth
      ? `@media (max-width: 1024px) {
        max-width: ${$auxImageMobileMaxWidth};
      }
    `
      : null}
`;
const FSAHSANotice = styled.p`
  ${TEXT_STYLES[TextVariant.UpperText3]}
  color: inherit;
`;
